import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import ProgramEmpty from "@images/empty/program_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramFilter } from "@modules/programs/types/programTypes"
import { TrainingProgramsFilters } from "@modules/training/components/trainingProgramsFilter"
import { EmptyView } from "@modules/ui/components/emptyView"
import { Plus, Search } from "lucide-react"

type TrainingProgramsFiltersViewProps = {
  allowProgramSelection: boolean
  className?: string
} & React.ComponentProps<"div">

export function TrainingProgramsFiltersView({
  allowProgramSelection,
  className,
  ...props
}: TrainingProgramsFiltersViewProps) {
  const userProgramsAsync = usePrograms({ programFilter: ProgramFilter.OWNED })
  const subscribedProgramsAsync = usePrograms({ programFilter: ProgramFilter.SUBSCRIBED })
  const sharedCoachProgramsAsync = usePrograms({ programFilter: ProgramFilter.SHARED_COACH })
  const { navigateToProgramCreationTypeSelection } = useProgramSidebar()
  const mySpaceNavigate = useMySpaceNavigate()

  const onCreateProgramClick = () => {
    navigateToProgramCreationTypeSelection()
  }
  const onDiscoverClick = () => {
    mySpaceNavigate(Path.MySpace.Explore)
  }

  return (
    <div className={cn("flex w-full flex-col p-5", className)} {...props}>
      <div className="mb-4 flex w-full items-center justify-between">
        <Text fontStyle="HM">{word("global.programs")}</Text>
        {(userProgramsAsync.data.content.length > 0 || subscribedProgramsAsync.data.content.length > 0) && (
          <Button variant="primary" size="lg" onClick={onCreateProgramClick}>
            <Plus />
            {word("global.createNew")}
          </Button>
        )}
      </div>

      {userProgramsAsync.data.content.length === 0 &&
      subscribedProgramsAsync.data.content.length === 0 &&
      sharedCoachProgramsAsync.data.content.length === 0 ? (
        <div className="flex grow items-center justify-center">
          <EmptyView
            image={{ src: ProgramEmpty }}
            title={word("programsSubscribed.empty.title")}
            description={word("programs.empty.description")}
            flexGrow={false}
            className="bg-surface-background p-6"
            button={
              <div className="flex flex-col items-center gap-3">
                <Button variant="primary" size="lg" onClick={onDiscoverClick}>
                  <Search />
                  {word("programs.discover")}
                </Button>

                <Text fontStyle="LL">{word("global.or")}</Text>

                <Button variant="outline" size="lg" onClick={onCreateProgramClick}>
                  <Plus />
                  {word("program.form.addProgram.title")}
                </Button>
              </div>
            }
            paddingType="In_table"
          />
        </div>
      ) : (
        <>
          <TrainingProgramsFilters
            programFilter={ProgramFilter.OWNED}
            allowProgramSelection={allowProgramSelection}
            title={word("programs.filter.owned")}
            emptyTitle={word("programs.empty.title")}
            emptyDescription={word("program.empty.description.noProgram")}
            emptyButtonLabel={word("program.form.addProgram.title")}
            emptyButtonAction={onCreateProgramClick}
            emptyButtonIcon={<Plus />}
            tagClassName="text-neutral-900 bg-neutral-100"
          />
          <TrainingProgramsFilters
            programFilter={ProgramFilter.SUBSCRIBED}
            allowProgramSelection={allowProgramSelection}
            title={word("programs.filter.subscribed")}
            emptyTitle={word("programsSubscribed.empty.title")}
            emptyDescription={word("programsSubscribed.empty.description")}
            emptyButtonLabel={word("programs.discover")}
            emptyButtonAction={onDiscoverClick}
            emptyButtonIcon={<Search />}
            tagClassName="text-surface-background bg-secondary-500"
          />
          <TrainingProgramsFilters
            programFilter={ProgramFilter.SHARED_COACH}
            allowProgramSelection={allowProgramSelection}
            title={word("programs.filter.sharedCoach")}
            tagClassName="text-neutral-900 border border-neutral-900"
          />
        </>
      )}
    </div>
  )
}
